import { Injectable } from '@angular/core'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  parseRoleModal,
  parseRoleNotification,
  RoleModalCode,
  RoleNotificationCode,
} from './libs/role-notification.lib'
import { Role } from './models/role.entity'
import { RoleSearchParams } from './models/role-search.model'
import { Page } from '../../models/util.model'
import { concatMap, from, last, Observable, scan } from 'rxjs'
import { RolesService } from './roles.service'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class RolesRepository {
  constructor(
    private rolesService: RolesService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search roles and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search roles
   */
  searchRoles$(
    searchParams: RoleSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Role>> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.rolesService.limit

    return needsPagination
      ? this._paginateRoles$(searchParams)
      : this._searchRoles$(searchParams, returnAll)
  }

  /**
   * Search roles with relative external data
   * @param searchParams - the search params
   * @param fields - the roles fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Role>> for search roles
   */
  private _searchRoles$(
    searchParams: RoleSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Role>> {
    return this.rolesService.search$(searchParams, returnAll)
  }

  /**
   * Search more than 200 roles with relative external data
   * @param searchParams - the search params
   * @param fields - the roles fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Role>> for search roles
   */
  private _paginateRoles$(
    searchParams: RoleSearchParams,
  ): Observable<Page<Role>> {
    const totalCount = searchParams.limit || this.rolesService.limit
    const pagesCount = Math.ceil(totalCount / this.rolesService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.rolesService.limit,
        offset: i * this.rolesService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchRoles$(pageParams)),
      scan<Page<Role>, Page<Role>>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
        }),
        { totalCount: 0, data: [] } as Page<Role>,
      ),
      last(),
    )
  }

  notify(options: { code?: RoleNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseRoleNotification(options.code),
    )
  }

  async alert(code: RoleModalCode, role?: Role): Promise<ModalResponse> {
    const settings = parseRoleModal(code, role)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
